import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Panel, PanelBody } from "../../components/panel/panel.jsx";
import { useComprobantes } from "../../hooks_cache/useComprobantes.js";
import { parseISO, format } from "date-fns";
import { formatCurrency } from "../../utils/utils_number.js";
import Chart from "chart.js/auto";
import { Bar } from "react-chartjs-2";

const ResumenWidget = ({ title, monto, cantidad, icon, color }) => (
  <div className="col-xl-4 col-md-6">
    <div className={`widget widget-stats ${color}`}>
      <div className="stats-icon stats-icon-lg">
        <i className={`fa ${icon} fa-fw`}></i>
      </div>
      <div className="stats-content">
        <div className="stats-title">{title}</div>
        <div className="stats-number">{formatCurrency(monto)}</div>
        <div className="stats-desc">{cantidad} comprobantes</div>
      </div>
    </div>
  </div>
);

const agruparComprobantesPorMes = (comprobantes) => {
  const resumenMensual = {};
  comprobantes.forEach((comp) => {
    const fecha = parseISO(comp.fecEmi);
    const mesClave = format(fecha, "MM/yyyy");

    if (!resumenMensual[mesClave]) {
      resumenMensual[mesClave] = {
        mes: mesClave,
        totalGravada: 0,
        totalNoSuj: 0,
        totalExenta: 0,
        totalPagar: 0,
      };
    }

    resumenMensual[mesClave].totalGravada += comp.totalGravada || 0;
    resumenMensual[mesClave].totalNoSuj += comp.totalNoSuj || 0;
    resumenMensual[mesClave].totalExenta += comp.totalExenta || 0;
    resumenMensual[mesClave].totalPagar += comp.totalPagar || 0;
  });

  return Object.values(resumenMensual).sort((a, b) => new Date(`01/${a.mes}`) - new Date(`01/${b.mes}`));
};

const fechaMinima = new Date();
fechaMinima.setMonth(fechaMinima.getMonth() - 12);
fechaMinima.setDate(1);
const fechaMinimaStr = fechaMinima.toISOString().split("T")[0];

const Home = () => {
  const { data: comprobantes } = useComprobantes();

  const comprobantesPendientes = useMemo(
    () => comprobantes?.filter((c) => c.estado === "pendiente" && c.TipoDTE.codigo !== "05") || [],
    [comprobantes]
  );

  const comprobantes12Meses = useMemo(
    () => comprobantes?.filter((c) => c.TipoDTE.codigo !== "05" && c.fecEmi >= fechaMinimaStr) || [],
    [comprobantes]
  );

  const hoy = new Date();

  const vencidosOrdenados = useMemo(
    () =>
      comprobantesPendientes
        .filter((c) => new Date(c.fecha_vencimiento) < hoy)
        .sort((a, b) => new Date(a.fecha_vencimiento) - new Date(b.fecha_vencimiento))
        .slice(0, 10),
    [comprobantesPendientes]
  );

  const resumen = useMemo(() => {
    return comprobantesPendientes.reduce(
      (acc, c) => {
        const pendiente = c.pendiente || 0;
        const vencimiento = new Date(c.fecha_vencimiento);

        acc.pendientes.cantidad += 1;
        acc.pendientes.monto += pendiente;

        if (vencimiento < hoy) {
          acc.vencidos.cantidad += 1;
          acc.vencidos.monto += pendiente;
        } else {
          acc.aVencer.cantidad += 1;
          acc.aVencer.monto += pendiente;
        }

        return acc;
      },
      {
        pendientes: { cantidad: 0, monto: 0 },
        vencidos: { cantidad: 0, monto: 0 },
        aVencer: { cantidad: 0, monto: 0 },
      }
    );
  }, [comprobantesPendientes]);

  const resumenMensualArray = useMemo(() => agruparComprobantesPorMes(comprobantes12Meses), [comprobantes12Meses]);

  const labels = resumenMensualArray.map((item) => item.mes);
  const dataGravada = resumenMensualArray.map((item) => item.totalGravada);
  const dataExenta = resumenMensualArray.map((item) => item.totalExenta);
  const dataNoSuj = resumenMensualArray.map((item) => item.totalNoSuj);

  const chartData = {
    labels,
    datasets: [
      {
        label: "Ventas Sujetas",
        data: dataGravada,
        backgroundColor: "rgba(144, 238, 144, 0.6)",
        borderColor: "rgba(60, 179, 113, 1)",
        borderWidth: 1,
      },
      {
        label: "Ventas Exentas",
        data: dataExenta,
        backgroundColor: "rgba(173, 216, 230, 0.6)",
        borderColor: "rgba(100, 149, 237, 1)",
        borderWidth: 1,
      },
      {
        label: "Ventas No Sujetas",
        data: dataNoSuj,
        backgroundColor: "rgba(255, 182, 193, 0.6)",
        borderColor: "rgba(255, 105, 180, 1)",
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      tooltip: {
        callbacks: {
          afterBody: (items) => {
            const index = items[0].dataIndex;
            const ventaTotal = (dataGravada[index] || 0) + (dataExenta[index] || 0) + (dataNoSuj[index] || 0);
            const iva = (dataGravada[index] || 0) * 0.13;
            const pagoCuenta = (dataGravada[index] || 0) * 0.0175;

            return [
              `💵 Venta general: $${ventaTotal.toFixed(2)}`,
              `🧾 IVA (13%): $${iva.toFixed(2)}`,
              `💰 Pago a Cuenta (1.75%): $${pagoCuenta.toFixed(2)}`,
            ];
          },
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (value) => `$${value.toLocaleString()}`,
        },
      },
    },
  };

  return (
    <div>
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item">
          <Link to="/">Home</Link>
        </li>
        <li className="breadcrumb-item active">Home</li>
      </ol>
      <h1 className="page-header">ANÁLISIS DE VENTAS</h1>

      <div className="row">
        <ResumenWidget
          title="POR COBRAR"
          monto={resumen.aVencer.monto}
          cantidad={resumen.aVencer.cantidad}
          icon="fa-dollar-sign"
          color="bg-teal"
        />
        <ResumenWidget
          title="VENCIDOS"
          monto={resumen.vencidos.monto}
          cantidad={resumen.vencidos.cantidad}
          icon="fa-warning"
          color="bg-red"
        />
        <ResumenWidget
          title="PENDIENTES"
          monto={resumen.pendientes.monto}
          cantidad={resumen.pendientes.cantidad}
          icon="fa-dollar-sign"
          color="bg-dark"
        />
      </div>

      <Panel>
        <PanelBody>
          <div className="row">
            <div className="col-md-12" style={{ height: "270px", width: "100%" }}>
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </PanelBody>
      </Panel>

      <div className="col-12 col-xl-7 col-md-7">
        <div className="mb-10px mt-10px fs-10px">
          <b className="text-dark">TOP 10 - COMPROBANTES VENCIDOS MÁS ANTIGUOS</b>
        </div>
        <ul className="list-group">
          {vencidosOrdenados.map((c) => (
            <li key={c.id} className="list-group-item d-flex justify-content-between align-items-center">
              <div>
                <strong>{c.numeroControl}</strong> <br />
                <small>Vence: {format(new Date(c.fecha_vencimiento), "dd/MM/yyyy")}</small>
              </div>
              <span className="badge bg-danger rounded-pill">{formatCurrency(c.pendiente)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Home;
